/**
 * Tools hub — three PLG micro-tools that capture leads at the top of
 * the funnel. Single route file with route-key view switching to keep
 * the bundle small.
 *
 * Routes:
 *   /tools                          → hub landing
 *   /tools/rent-estimate            → instant rent estimate calculator
 *   /tools/tenant-screening         → 12-step checklist (email-gated PDF)
 *   /tools/late-rent-guide       → province-specific guide (gated)
 */
import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { Calculator, ShieldCheck, FileWarning, Cloud, Download, ArrowRight, Sparkles, Loader2, CheckCircle2 } from "lucide-react";
import PublicHeader from "@/components/layout/PublicHeader";
import SmartChatBubble from "@/components/SmartChatBubble";
import { useBotShield, BotHoneypot } from "@/lib/botShield";

const API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");

function setMeta(sel, attrs) {
  let el = document.head.querySelector(sel);
  if (!el) { el = document.createElement(sel.startsWith("link") ? "link" : "meta"); document.head.appendChild(el); }
  Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
  return el;
}

// ─────────────────────────── Hub ──────────────────────────
// Public free-toolkit tiles. Three growth tools (Prospect Discovery,
// AI Search Optimizer, Auto copilot) have been moved into the paid
// Landlord portal and are only accessible to active subscribers via a
// private link — not surfaced here.
const TOOLS = [
  { slug: "rent-estimate",         Icon: Calculator,   title: "Rent Estimate Calculator", desc: "Median monthly rent for any Canadian city + bed count. Free.", tag: "Pricing", color: "emerald" },
  { slug: "tenant-screening",      Icon: ShieldCheck,  title: "Tenant Screening Checklist", desc: "12-step process to screen tenants legally. Free PDF.",         tag: "Compliance", color: "violet" },
  { slug: "late-rent-guide",    Icon: FileWarning,  title: "Late Rent Recovery Guide", desc: "Province-specific 3-step recovery plan + demand letter.",     tag: "Recovery", color: "rose" },
];

export function ToolsHub() {
  useEffect(() => {
    document.title = "Free landlord tools — Central Rentals";
    const tags = [
      setMeta('meta[name="description"]', { name: "description", content: "Free tools for Canadian landlords: instant rent estimate, 12-step tenant screening checklist, province-specific late-rent recovery guide." }),
      setMeta('link[rel="canonical"]', { rel: "canonical", href: "https://centralrentals.ca/tools" }),
    ];
    return () => tags.forEach((t) => t?.remove?.());
  }, []);
  return (
    <div className="min-h-screen bg-white" data-testid="tools-hub-page">
      <PublicHeader light/>
      <section className="max-w-6xl mx-auto px-5 pt-12 pb-10">
        <div className="inline-flex items-center gap-2 mb-5 px-3 py-1.5 rounded-full bg-emerald-50 border border-emerald-200 text-[11px] font-bold tracking-wide text-emerald-800">
          <Sparkles className="w-3.5 h-3.5"/> Free · Cancel any time · Built by Mark
        </div>
        <h1 className="font-display text-4xl sm:text-5xl md:text-6xl font-extrabold tracking-tighter text-slate-900">
          Free tools for Canadian landlords.
        </h1>
        <p className="mt-5 text-lg text-slate-600 max-w-2xl">
          Three precision tools that solve a specific landlord job in
          under 60 seconds. Use them as often as you like — and when
          you&apos;re ready, the full platform is a Paid plans from CA$35/month away.
        </p>
        <div className="mt-10 grid md:grid-cols-3 gap-5" data-testid="tools-hub-grid">
          {TOOLS.map(({ slug, Icon, title, desc, tag, color, external }) => {
            const to = external ? slug : `/tools/${slug}`;
            const key = external ? slug.replace(/^\//, "") : slug;
            return (
              <Link key={key} to={to} data-testid={`tools-hub-${key}`} className="group rounded-2xl border border-slate-200 bg-white p-6 shadow-sm hover:shadow-md hover:border-slate-300 transition-all">
                <div className={`inline-flex items-center justify-center w-10 h-10 rounded-xl bg-${color}-100 text-${color}-700 mb-4`}>
                  <Icon className="w-5 h-5"/>
                </div>
                <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-1">{tag}</div>
                <div className="font-display text-lg font-extrabold text-slate-900 group-hover:text-emerald-700">{title}</div>
                <p className="mt-1 text-sm text-slate-500">{desc}</p>
                <div className="mt-4 inline-flex items-center gap-1 text-xs font-bold text-emerald-700">
                  Open tool <ArrowRight className="w-3 h-3"/>
                </div>
              </Link>
            );
          })}
        </div>
      </section>
      <SmartChatBubble/>
    </div>
  );
}

// ─────────────────────────── Email gate ──────────────────────────
function EmailGate({ tool, province, payload, onUnlocked }) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const shield = useBotShield();
  async function submit() {
    if (!email.trim()) return;
    setBusy(true); setError("");
    try {
      const utm = new URLSearchParams(window.location.search);
      const r = await fetch(`${API}/api/plg/lead-magnet`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: email.trim(), name: name.trim() || null,
          tool, province, payload,
          utm_source: utm.get("utm_source"), utm_medium: utm.get("utm_medium"), utm_campaign: utm.get("utm_campaign"),
          gclid: utm.get("gclid"), fbclid: utm.get("fbclid"),
          ...shield.values(),
        }),
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j.detail || "Submit failed");
      }
      const d = await r.json();
      try {
        const { fireLeadFormSubmit } = await import("@/lib/google-tag");
        fireLeadFormSubmit({ source: `plg_${tool}`, email: email.trim(), valueCad: 10, transactionId: d.lead_id });
      } catch { /* never block UX */ }
      onUnlocked(d);
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  }
  return (
    <div className="rounded-2xl border-2 border-emerald-200 bg-gradient-to-br from-emerald-50 to-white p-6 max-w-md" data-testid="email-gate">
      <BotHoneypot {...shield.fieldProps} />
      <div className="font-display text-xl font-extrabold text-slate-900 mb-1">One last step</div>
      <p className="text-sm text-slate-600 mb-4">We&apos;ll email the PDF + bookmark this report for you.</p>
      <input data-testid="gate-name" placeholder="First name (optional)" value={name} onChange={(e) => setName(e.target.value)} className="w-full mb-2 px-3 py-2 text-sm border border-slate-300 rounded-lg outline-none focus:border-emerald-500"/>
      <input data-testid="gate-email" type="email" placeholder="you@email.com" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg outline-none focus:border-emerald-500"/>
      {error && <div className="text-xs text-rose-600 mt-2">{error}</div>}
      <button onClick={submit} disabled={busy || !email.trim()} data-testid="gate-submit" className="mt-3 w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-sm disabled:opacity-50">
        {busy ? <><Loader2 className="w-4 h-4 animate-spin"/> Sending…</> : <><Download className="w-4 h-4"/> Get my free download</>}
      </button>
      <p className="mt-2 text-[10px] text-slate-500 text-center">No spam · Unsubscribe in 1 click</p>
    </div>
  );
}

// ─────────────────────────── Rent Estimate Tool ──────────────────────────
export function RentEstimateTool() {
  const [provinces, setProvinces] = useState([]);
  const [form, setForm] = useState({ province: "ON", city: "toronto", bedrooms: 2, sqft: "" });
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [unlocked, setUnlocked] = useState(false);

  useEffect(() => {
    document.title = "Rent Estimate Calculator — Central Rentals";
    fetch(`${API}/api/plg/cities`).then((r) => r.json()).then((d) => setProvinces(d.provinces || [])).catch(() => {});
  }, []);

  const cities = (provinces.find((p) => p.code === form.province) || {}).cities || [];

  async function calc() {
    setBusy(true); setError(""); setResult(null);
    try {
      const r = await fetch(`${API}/api/plg/rent-estimate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, sqft: form.sqft ? Number(form.sqft) : null }),
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j.detail || "Calc failed");
      }
      setResult(await r.json());
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  }

  async function downloadPdf() {
    const r = await fetch(`${API}/api/plg/rent-estimate.pdf`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ...result, sqft: form.sqft ? Number(form.sqft) : null }),
    });
    if (r.ok) { const blob = await r.blob(); window.open(URL.createObjectURL(blob), "_blank"); }
  }

  return (
    <div className="min-h-screen bg-white" data-testid="rent-estimate-page">
      <PublicHeader light/>
      <section className="max-w-5xl mx-auto px-5 pt-12 pb-16 grid md:grid-cols-[1fr_1.3fr] gap-8">
        <div>
          <Link to="/tools" className="text-xs text-slate-500 hover:text-slate-900">← All tools</Link>
          <h1 className="mt-4 font-display text-3xl sm:text-4xl font-extrabold tracking-tighter text-slate-900">Rent Estimate Calculator</h1>
          <p className="mt-3 text-slate-600">Instant median monthly rent for 25 Canadian cities. Composite of CMHC, Rentals.ca and Zumper Q4 2025.</p>
          <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-5 space-y-3" data-testid="rent-form">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Province</label>
                <select data-testid="rent-province" value={form.province} onChange={(e) => setForm({ ...form, province: e.target.value, city: ((provinces.find((p) => p.code === e.target.value) || {}).cities || [""])[0] })} className="mt-1 w-full px-2.5 py-2 text-sm border border-slate-300 rounded-lg bg-white">
                  {provinces.map((p) => <option key={p.code} value={p.code}>{p.code}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">City</label>
                <select data-testid="rent-city" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="mt-1 w-full px-2.5 py-2 text-sm border border-slate-300 rounded-lg bg-white">
                  {cities.map((c) => <option key={c} value={c}>{c.replace(/-/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())}</option>)}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Bedrooms</label>
                <select data-testid="rent-bedrooms" value={form.bedrooms} onChange={(e) => setForm({ ...form, bedrooms: Number(e.target.value) })} className="mt-1 w-full px-2.5 py-2 text-sm border border-slate-300 rounded-lg bg-white">
                  <option value={0}>Studio</option>
                  <option value={1}>1 bedroom</option>
                  <option value={2}>2 bedroom</option>
                  <option value={3}>3 bedroom</option>
                  <option value={4}>4+ bedroom</option>
                </select>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Sqft (optional)</label>
                <input data-testid="rent-sqft" type="number" min={200} max={10000} value={form.sqft} onChange={(e) => setForm({ ...form, sqft: e.target.value })} className="mt-1 w-full px-2.5 py-2 text-sm border border-slate-300 rounded-lg outline-none focus:border-emerald-500"/>
              </div>
            </div>
            <button onClick={calc} disabled={busy} data-testid="rent-calc-btn" className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-sm disabled:opacity-50">
              {busy ? <><Loader2 className="w-4 h-4 animate-spin"/> Calculating…</> : <><Calculator className="w-4 h-4"/> Get my estimate</>}
            </button>
            {error && <div className="text-xs text-rose-600">{error}</div>}
          </div>
        </div>
        <div>
          {!result && <div className="rounded-2xl border-2 border-dashed border-slate-200 p-12 text-center text-slate-500">Your estimate will appear here.</div>}
          {result && (
            <div className="rounded-2xl border border-slate-200 bg-gradient-to-br from-emerald-50 via-white to-white p-6" data-testid="rent-result">
              <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Median monthly rent</div>
              <div className="font-display text-5xl font-extrabold text-slate-900 tabular-nums mt-1">CA$ {result.median_cad.toLocaleString()}</div>
              <div className="mt-3 text-sm text-slate-600">
                <span className="font-bold">10th percentile</span>: CA$ {result.p10_cad.toLocaleString()} ·
                <span className="font-bold"> 90th</span>: CA$ {result.p90_cad.toLocaleString()}
              </div>
              <div className="mt-2 text-xs text-slate-500">{result.source} · {result.as_of}</div>
              {result.fallback_provincial_avg && <div className="mt-2 text-xs text-amber-700 bg-amber-50 px-2 py-1 rounded">Used provincial average — exact city not in our table yet.</div>}
              {!unlocked ? (
                <div className="mt-5">
                  <EmailGate tool="rent-estimate" payload={result} onUnlocked={() => setUnlocked(true)}/>
                </div>
              ) : (
                <div className="mt-5 flex items-center gap-2 text-sm font-bold text-emerald-700">
                  <CheckCircle2 className="w-4 h-4"/> Unlocked.
                  <button onClick={downloadPdf} data-testid="rent-download-pdf" className="ml-2 inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-slate-900 text-white text-xs"><Download className="w-3 h-3"/> Download full PDF</button>
                </div>
              )}
            </div>
          )}
        </div>
      </section>
      <SmartChatBubble/>
    </div>
  );
}

// ─────────────────────────── Tenant Screening ──────────────────────────
export function TenantScreeningTool() {
  const [unlocked, setUnlocked] = useState(false);
  useEffect(() => { document.title = "Tenant Screening Checklist — Central Rentals"; }, []);
  return (
    <div className="min-h-screen bg-white" data-testid="tenant-screening-page">
      <PublicHeader light/>
      <section className="max-w-3xl mx-auto px-5 pt-12 pb-16">
        <Link to="/tools" className="text-xs text-slate-500 hover:text-slate-900">← All tools</Link>
        <h1 className="mt-4 font-display text-3xl sm:text-4xl font-extrabold tracking-tighter text-slate-900">Tenant Screening Checklist</h1>
        <p className="mt-3 text-slate-600 max-w-2xl">The 12-step process Canadian landlords use to find rock-solid tenants — and stay onside with the Human Rights Code. Includes red-flag list + 2-page PDF.</p>
        <ul className="mt-7 space-y-2 text-slate-700 text-sm" data-testid="tenant-steps-preview">
          {["Application form (legally compliant)","Photo ID + secondary","Income verification (3× rent rule)","Employment verification call","Credit check (with written consent)","Past 2 landlord references","Visual inspection during viewing","Cross-check social media","Pets + smoking policy upfront","Lease + last month's rent","Move-in inspection report","Day-1 communication"].map((s, i) => (
            <li key={i} className="flex items-start gap-2"><span className="flex-none w-5 h-5 rounded-full bg-violet-100 text-violet-700 text-[11px] font-bold inline-flex items-center justify-center">{i+1}</span><span>{s}</span></li>
          ))}
        </ul>
        <div className="mt-10">
          {!unlocked ? (
            <EmailGate tool="tenant-screening" onUnlocked={() => setUnlocked(true)}/>
          ) : (
            <a href={`${API}/api/plg/tenant-screening.pdf`} target="_blank" rel="noopener noreferrer" data-testid="tenant-download" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-slate-900 hover:bg-slate-800 text-white font-bold text-sm">
              <Download className="w-4 h-4"/> Download the full checklist PDF
            </a>
          )}
        </div>
      </section>
      <SmartChatBubble/>
    </div>
  );
}

// ─────────────────────────── Late Rent Guide ──────────────────────────
const GUIDE_PROVS = [
  { slug: "ontario", name: "Ontario" }, { slug: "british-columbia", name: "British Columbia" },
  { slug: "quebec", name: "Quebec" }, { slug: "alberta", name: "Alberta" },
  { slug: "nova-scotia", name: "Nova Scotia" }, { slug: "manitoba", name: "Manitoba" },
];

export function LateRentGuideTool() {
  const [province, setProvince] = useState("ontario");
  const [unlocked, setUnlocked] = useState(false);
  useEffect(() => { document.title = "Late Rent Recovery Guide — Central Rentals"; }, []);
  return (
    <div className="min-h-screen bg-white" data-testid="late-rent-page">
      <PublicHeader light/>
      <section className="max-w-3xl mx-auto px-5 pt-12 pb-16">
        <Link to="/tools" className="text-xs text-slate-500 hover:text-slate-900">← All tools</Link>
        <h1 className="mt-4 font-display text-3xl sm:text-4xl font-extrabold tracking-tighter text-slate-900">Late Rent Recovery Guide</h1>
        <p className="mt-3 text-slate-600 max-w-2xl">Step-by-step plan to recover unpaid rent the legal way in your province. Includes a templated demand letter you can paste straight into a registered-mail envelope.</p>
        <div className="mt-7" data-testid="late-rent-province-select">
          <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Your province</label>
          <select data-testid="late-rent-province" value={province} onChange={(e) => { setProvince(e.target.value); setUnlocked(false); }} className="mt-1 w-full md:w-72 px-3 py-2 text-sm border border-slate-300 rounded-lg bg-white">
            {GUIDE_PROVS.map((p) => <option key={p.slug} value={p.slug}>{p.name}</option>)}
          </select>
        </div>
        <div className="mt-7 rounded-xl bg-slate-50 border border-slate-200 p-5 text-sm text-slate-700">
          <div className="font-bold text-slate-900 mb-1">What&apos;s inside the PDF</div>
          <ol className="space-y-1 list-decimal list-inside">
            <li>Formal notice — the exact form to serve in your province</li>
            <li>Tribunal application — fees + online filing link</li>
            <li>Hearing + enforcement — what to bring + how to enforce the order</li>
            <li>Templated demand letter ready to send by registered mail</li>
          </ol>
        </div>
        <div className="mt-8">
          {!unlocked ? (
            <EmailGate tool="late-rent" province={province} onUnlocked={() => setUnlocked(true)}/>
          ) : (
            <a href={`${API}/api/plg/late-rent/${province}.pdf`} target="_blank" rel="noopener noreferrer" data-testid="late-rent-download" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-slate-900 hover:bg-slate-800 text-white font-bold text-sm">
              <Download className="w-4 h-4"/> Download the {GUIDE_PROVS.find((p) => p.slug === province)?.name} guide
            </a>
          )}
        </div>
      </section>
      <SmartChatBubble/>
    </div>
  );
}

// ─────────────────────────── Dispatcher ──────────────────────────
export default function ToolsRoute() {
  const { tool } = useParams();
  const nav = useNavigate();
  if (!tool) return <ToolsHub/>;
  if (tool === "rent-estimate") return <RentEstimateTool/>;
  if (tool === "tenant-screening") return <TenantScreeningTool/>;
  if (tool === "late-rent-guide") return <LateRentGuideTool/>;
  // Unknown tool → bounce to hub.
  nav("/tools", { replace: true });
  return null;
}
